import { AppointmentService } from "../../services/AppointmentService";
import { QmsService } from "../../services/QmsService";
import { extractApiError } from "../../utils/extractApiError";
import { generateIdempotencyKey } from "../../utils/idempotencyKey";
import { resolveSystemTypeConfig } from "../../utils/resolveSystemTypeConfig";
import { resolveWorkspaceFeatureAccess } from "../../utils/workspaceFeatures";
import { toPatientGender } from "../../utils/patientGender";
import { joinedPhoneOrUndefined, patientEmailFor, verifiedContactFields, } from "./verifiedContact";
import { followUpBookingError, FOLLOW_UP_RESET } from "./followUpSlice";
import { bookedChargeAmount, buildBookedAppointment, buildBookedRecord, queueVisitCharge, } from "./bookedAppointment";
import { quoteCharge, requireQuotedCharge } from "./chargeQuote";
import { buildQueueToken, formatDateStr, } from "./queueToken";
const DISPLAY_DAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
const DISPLAY_MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
function buildPatientPayload(state) {
    const { patientTab, selectedPatient, newPatientForm } = state;
    const contact = verifiedContactFields(state);
    if (patientTab === "existing" && selectedPatient) {
        return {
            id: selectedPatient.id,
            firstName: selectedPatient.firstName,
            lastName: selectedPatient.lastName || "",
            email: patientEmailFor(state, selectedPatient.email),
            ...contact,
            age: selectedPatient.age || 0,
            gender: toPatientGender(selectedPatient.gender),
        };
    }
    const nameParts = newPatientForm.fullName.trim().split(/\s+/);
    return {
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: patientEmailFor(state, newPatientForm.email),
        ...contact,
        age: newPatientForm.age ? parseInt(newPatientForm.age, 10) : 0,
        gender: toPatientGender(newPatientForm.gender),
    };
}
function getSlotDurationMins(slot) {
    const toMinutes = (value) => {
        const time = value.includes("T") ? value.split("T").pop() : value;
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
    };
    const duration = toMinutes(slot.end) - toMinutes(slot.start);
    return duration > 0 ? duration : 0;
}
function buildPatientAddress(state) {
    const { patientTab, newPatientForm, countryCode, otpChannel } = state;
    const city = patientTab === "new" ? newPatientForm.city.trim() || "N/A" : "N/A";
    return {
        addressLine1: patientTab === "new" ? newPatientForm.address || "" : "",
        city,
        state: "",
        country: otpChannel !== "email" && countryCode === "+91" ? "India" : "",
        zipcode: patientTab === "new" ? newPatientForm.zipCode || "" : "",
        ...verifiedContactFields(state),
    };
}
const BOOKING_TYPE_BY_CHARGE = {
    ACTIVE_PACK: "USE_ACTIVE_PACKAGE",
    PACK_PURCHASE: "PACKAGE_PURCHASE",
    FOLLOW_UP: "ONE_TIME_APPOINTMENT",
    CONSULTATION: "ONE_TIME_APPOINTMENT",
};
function buildAppointmentPayload(state) {
    const { workspace, selectedDoctor, selectedAddressId, consultationMode, selectedDate, selectedSlot, selectedTimezone, selectedPack, selectedActiveSessionPack, } = state;
    if (!workspace || !selectedDoctor || !selectedAddressId || !selectedSlot) {
        throw new Error("Missing required booking data");
    }
    const dateStr = formatDateStr(selectedDate);
    const fromDateTimeTs = selectedSlot.start.includes("T")
        ? selectedSlot.start
        : `${dateStr}T${selectedSlot.start}:00`;
    const toDateTimeTs = selectedSlot.end.includes("T")
        ? selectedSlot.end
        : `${dateStr}T${selectedSlot.end}:00`;
    const charge = requireQuotedCharge(state);
    const consultationCharge = bookedChargeAmount(charge);
    const payload = {
        workspaceId: workspace.workspaceId,
        workspaceAddressId: selectedAddressId,
        addressId: selectedAddressId,
        doctorId: selectedDoctor.id,
        mode: consultationMode,
        appointmentDate: dateStr,
        fromDateTimeTs,
        toDateTimeTs,
        durationMins: getSlotDurationMins(selectedSlot),
        consultationCharge,
        bookingType: BOOKING_TYPE_BY_CHARGE[charge.kind],
        paymentMode: "CASH",
        patientTimezone: selectedTimezone,
        patientPayload: buildPatientPayload(state),
        patientAddress: buildPatientAddress(state),
        currency: charge.kind === "ACTIVE_PACK" ? undefined : charge.currency,
        source: "SDK_POWERED_WEBSITE",
        type: "CONSULTATION",
        paymentAmount: consultationCharge,
    };
    if (charge.kind === "ACTIVE_PACK" && selectedActiveSessionPack) {
        payload.patientPackageId = selectedActiveSessionPack.id;
        payload.packageConfigId = selectedActiveSessionPack.packageConfigId;
        payload.packageAmount = 0;
    }
    else if (charge.kind === "PACK_PURCHASE" && selectedPack) {
        payload.packageConfigId = selectedPack.id;
        payload.packageAmount = consultationCharge;
    }
    else if (charge.kind === "FOLLOW_UP") {
        payload.followUp = true;
    }
    return payload;
}
function buildQmsPayload(state) {
    const { workspace, selectedDoctor, selectedAddressId, selectedDate, selectedSlot, patientTab, selectedPatient, newPatientForm, } = state;
    if (!workspace || !selectedDoctor || !selectedAddressId || !selectedSlot) {
        throw new Error("Missing required QMS booking data");
    }
    const base = {
        workspaceId: workspace.workspaceId,
        addressId: selectedAddressId,
        doctorId: selectedDoctor.id,
        qmsWorkShiftId: Number(selectedSlot.id),
        appointmentDate: formatDateStr(selectedDate),
        durationInSeconds: 900,
        patientPhoneNumber: joinedPhoneOrUndefined(state),
        totalFee: 0,
        isInternational: state.feeAudience === "INTERNATIONAL",
        type: "CONSULTATION",
        mode: "OFFLINE",
        paymentMode: "CASH",
        source: "SDK_POWERED_WEBSITE",
    };
    if (patientTab === "existing") {
        if (!selectedPatient) {
            throw new Error("Patient must be selected for QMS booking");
        }
        return { ...base, patientId: selectedPatient.id };
    }
    const nameParts = newPatientForm.fullName.trim().split(/\s+/);
    if (!nameParts[0]) {
        throw new Error("Patient name is required for QMS booking");
    }
    return {
        ...base,
        patientPayload: {
            firstName: nameParts[0],
            lastName: nameParts.slice(1).join(" ") || undefined,
            ...verifiedContactFields(state),
            email: patientEmailFor(state, newPatientForm.email) || undefined,
            age: newPatientForm.age ? parseInt(newPatientForm.age, 10) : undefined,
            gender: newPatientForm.gender
                ? toPatientGender(newPatientForm.gender)
                : undefined,
        },
    };
}
async function fileDraftClaim(state, appointmentId, amount, currency) {
    const choice = state.selectedPaymentMethod;
    if (appointmentId == null || choice.kind !== "MANUAL")
        return;
    const reference = state.claimDraftReference.trim();
    const proofFile = state.claimDraftProof;
    if (!reference && !proofFile)
        return;
    const method = state.availablePaymentMethods.find((m) => m.id === choice.methodId);
    state.beginPaymentClaim();
    await state.submitPaymentClaim({
        appointmentId,
        paymentMethodId: choice.methodId,
        provider: method?.provider ?? method?.kind ?? null,
        paymentReference: reference || null,
        amount,
        currency,
        proofFile,
    });
}
export const createBookingSlice = (set, get) => ({
    bookingLoading: false,
    bookingError: null,
    appointmentId: null,
    queueToken: null,
    bookedAppointment: null,
    setBookingError: (msg) => set({ bookingError: msg }),
    submitBooking: async () => {
        const state = get();
        const config = resolveSystemTypeConfig(state.activeSystemType);
        const features = resolveWorkspaceFeatureAccess(state.workspace);
        set({ bookingLoading: true, bookingError: null });
        try {
            if (config.bookingMode === "qms") {
                if (!features.qms) {
                    throw new Error("Queue booking is not enabled for this workspace");
                }
                const payload = buildQmsPayload(state);
                const qmsKey = state.qmsIdempotencyKey ?? generateIdempotencyKey();
                if (state.qmsIdempotencyKey !== qmsKey) {
                    set({ qmsIdempotencyKey: qmsKey });
                }
                const response = await QmsService.bookAppointment(payload, qmsKey);
                const token = buildQueueToken(state, {
                    id: response.data?.id,
                    tokenNumber: response.data?.tokenNumber,
                    queuePosition: response.data?.queuePosition,
                    estimatedWaitTime: response.data?.estimatedWaitTime,
                });
                const qmsAppointmentId = response.data?.id ?? null;
                const qmsCharge = queueVisitCharge(quoteCharge(state), response.data);
                set({
                    queueToken: token,
                    appointmentId: qmsAppointmentId,
                    bookedAppointment: qmsCharge
                        ? buildBookedRecord(state, qmsCharge)
                        : null,
                    currentStep: "queue-confirmation",
                });
                get().bumpBookingRevision();
                await fileDraftClaim(get(), qmsAppointmentId, qmsCharge ? bookedChargeAmount(qmsCharge) : null, qmsCharge && qmsCharge.kind !== "ACTIVE_PACK"
                    ? qmsCharge.currency
                    : null);
            }
            else {
                if (!features.appointment) {
                    throw new Error("Appointment booking is not enabled for this workspace");
                }
                const payload = buildAppointmentPayload(state);
                const response = await AppointmentService.createUnifiedAppointment(payload);
                const wrapped = response?.data;
                const bookedId = typeof response?.id === "number"
                    ? response.id
                    : typeof wrapped?.id === "number"
                        ? wrapped.id
                        : null;
                set({
                    appointmentId: bookedId,
                    bookedAppointment: buildBookedAppointment(state, payload),
                    currentStep: "success",
                });
                get().bumpBookingRevision();
                await fileDraftClaim(get(), bookedId, payload.consultationCharge != null
                    ? Number(payload.consultationCharge)
                    : null, payload.currency ?? null);
            }
        }
        catch (e) {
            set({
                bookingError: followUpBookingError(e) ??
                    extractApiError(e, "Booking failed. Please try again."),
            });
        }
        finally {
            set({ bookingLoading: false });
        }
    },
    resetBooking: () => {
        const popup = get().paymentPopup;
        if (popup && !popup.closed) {
            try {
                popup.close();
            }
            catch {
            }
        }
        set({
            bookingLoading: false,
            bookingError: null,
            appointmentId: null,
            queueToken: null,
            bookedAppointment: null,
            selectedSlot: null,
            selectedDuration: null,
            selectedPack: null,
            selectedPatient: null,
            selectedActiveSessionPack: null,
            newPatientForm: {
                fullName: "",
                gender: "",
                city: "",
                age: "",
                email: "",
                address: "",
                zipCode: "",
            },
            patientTab: "existing",
            currentStep: "schedule",
            ...FOLLOW_UP_RESET,
            paymentStatus: "idle",
            paymentSessionRef: null,
            paymentAppointmentRef: null,
            paymentCheckoutUrl: null,
            paymentError: null,
            paymentFailureReason: null,
            paymentPopup: null,
            selectedPaymentMethod: { kind: "AT_CLINIC" },
            claimStatus: "idle",
            claimError: null,
            claimReference: null,
            claimProofUrl: null,
            claimIdempotencyKey: null,
            qmsIdempotencyKey: null,
        });
    },
});
